import * as React from 'react';
import { Route, Switch } from 'react-router-dom';

import { NotFound } from 'src/ui/components/NotFound';
import { PrivateRoute } from 'src/ui/components/PrivateRoute';
import { ManageApiTokens } from 'src/ui/containers/api-tokens/ManageApiTokens';
import { Configuration } from 'src/ui/containers/dashboard/Configuration';
import { Diagnostic } from 'src/ui/containers/diagnostic/Diagnostic';
import { Connect } from 'src/ui/containers/dialogs/Connect';
import { PortalEndpoint } from 'src/ui/containers/endpoints/PortalEndpoint';
import { PortalEndpoints } from 'src/ui/containers/endpoints/PortalEndpoints';
import { GetStarted } from 'src/ui/containers/get-started/GetStarted';
import { CloudAdmin } from 'src/ui/containers/internal/CloudAdmin';
import { CloudReport } from 'src/ui/containers/internal/CloudReport';
import { Home as EmployeeHome } from 'src/ui/containers/internal/Home';
import { UserDashboard } from 'src/ui/containers/internal/UserDashboard';
import { UserSearch } from 'src/ui/containers/internal/UserSearch';
import { Invitation } from 'src/ui/containers/invitation/Invitation';
import { LandingPage } from 'src/ui/containers/landing/LandingPage';
import { Login } from 'src/ui/containers/Login';
import { KitOverview } from 'src/ui/containers/marketplace/KitOverview';
import { MPHome } from 'src/ui/containers/marketplace/MPHome';
import { OrganizationDashboard } from 'src/ui/containers/organization/OrganizationDashboard';
import { OrganizationEndpoint } from 'src/ui/containers/organization/OrganizationEndpoint';
import { OrganizationEndpoints } from 'src/ui/containers/organization/OrganizationEndpoints';
import { OrganizationSduDashboard } from 'src/ui/containers/organization/OrganizationSduDashboard';
import { OrganizationSduDashboardEndpoint } from 'src/ui/containers/organization/OrganizationSduDashboardEndpoint';
import { Databricks } from 'src/ui/containers/partner/databricks/Databricks';
import { Share } from 'src/ui/containers/share/Share';
import { VerifyChallengePage } from 'src/ui/containers/VerifyChallengePage';
import { VerifyEmailPage } from 'src/ui/containers/VerifyEmailPage';
import { VoiceboxPage } from 'src/ui/containers/voicebox/VoiceboxPage';
import { isPublicApiV2Enabled } from 'src/ui/utils/features';

export const AppBody: React.VFC = () => {
  const uuidRegex =
    '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
  return (
    <div className="sd-app-body">
      <div className="sd-vertical">
        <Switch>
          <Route path="/login/:customer?">
            <Login />
          </Route>
          <Route path="/verify-email">
            <VerifyEmailPage />
          </Route>
          <Route path="/diagnostic">
            <Diagnostic />
          </Route>
          <PrivateRoute adminOnly path="/internal/cloud/admin">
            <CloudAdmin />
          </PrivateRoute>
          <PrivateRoute path="/internal/cloud/report/:days([\d]+)" staffOnly>
            <CloudReport />
          </PrivateRoute>
          <PrivateRoute path="/internal/cloud/report" staffOnly>
            <CloudReport />
          </PrivateRoute>
          <PrivateRoute path={`/internal/user/:id(${uuidRegex})`} staffOnly>
            <UserDashboard />
          </PrivateRoute>
          <PrivateRoute path="/internal/user/search" staffOnly>
            <UserSearch />
          </PrivateRoute>
          <PrivateRoute path="/internal" staffOnly>
            <EmployeeHome />
          </PrivateRoute>
          <PrivateRoute
            path={[
              '/get-started/:flavor([w]+)',
              // TODO: public orgs support (cloud purchase)
              // '/o/:org([a-z0-9-]+)/get-started/:flavor([w]+)'
            ]}
          >
            <GetStarted />
          </PrivateRoute>
          <PrivateRoute
            path={[
              '/get-started',
              // TODO: public orgs support (cloud purchase)
              // '/o/:org([a-z0-9-]+)/get-started'
            ]}
          >
            <GetStarted />
          </PrivateRoute>
          <PrivateRoute path="/connect">
            <Connect />
          </PrivateRoute>
          <PrivateRoute
            path={[
              '/u/:id(\\d+)/configuration',
              '/o/:org([a-z0-9-]+)/:id(\\d+)/configuration',
            ]}
          >
            <Configuration />
          </PrivateRoute>
          <PrivateRoute
            path={[
              '/u/:id(\\d+)/diagnostic',
              '/o/:org([a-z0-9-]+)/:id(\\d+)/diagnostic',
            ]}
          >
            <Diagnostic />
          </PrivateRoute>
          <PrivateRoute
            exact
            path={[
              '/u/:id(\\d+)/voicebox/:conversation?',
              '/o/:org([a-z0-9-]+)/:id(\\d+)/voicebox/:conversation?',
            ]}
            voiceboxOnly
          >
            <VoiceboxPage />
          </PrivateRoute>
          <PrivateRoute
            exact
            path={['/u/:id(\\d+|demo)/', '/o/:org([a-z0-9-]+)/:id(\\d+)/']}
          >
            <LandingPage />
          </PrivateRoute>
          <PrivateRoute exact path="/kits">
            <MPHome />
          </PrivateRoute>
          <PrivateRoute exact path="/kits/:id([\w:\d-\.%]+)">
            <KitOverview />
          </PrivateRoute>
          <PrivateRoute
            exact
            path={[
              '/keys',
              // TODO: public orgs support (api tokens)
              // '/o/:org([a-z0-9-]+)/keys',
            ]}
            voiceboxAPIAccessEnabled={!isPublicApiV2Enabled()}
          >
            <ManageApiTokens />
          </PrivateRoute>
          <PrivateRoute exact path="/">
            <LandingPage />
          </PrivateRoute>
          <PrivateRoute exact path="/o/:org([a-z0-9-]+)/">
            <OrganizationDashboard />
          </PrivateRoute>
          <PrivateRoute exact path="/connections">
            <PortalEndpoints />
          </PrivateRoute>
          <PrivateRoute exact path="/connections/:id(\d+)">
            <PortalEndpoint />
          </PrivateRoute>
          <PrivateRoute exact path="/o/:org([a-z0-9-]+)/connections">
            <OrganizationEndpoints />
          </PrivateRoute>
          <PrivateRoute exact path="/o/:org([a-z0-9-]+)/connections/:id(\d+)">
            <OrganizationEndpoint />
          </PrivateRoute>
          <PrivateRoute exact path="/o/:org([a-z0-9-]+)/units">
            <OrganizationSduDashboard />
          </PrivateRoute>
          <PrivateRoute exact path="/o/:org([a-z0-9-]+)/units/:id(\d+)">
            <OrganizationSduDashboardEndpoint />
          </PrivateRoute>
          {
            // TODO: do we still support databricks partner connect?
          }
          <PrivateRoute path="/databricks-partner-home">
            <Databricks />
          </PrivateRoute>
          <PrivateRoute path="/share/:hash([\w\d]+)">
            <Share />
          </PrivateRoute>
          <Route path="/verify-challenge">
            <VerifyChallengePage />
          </Route>
          <Route path="/invitation/:id([\w\d-]+)">
            <Invitation />
          </Route>
          <Route path="*">
            <NotFound />
          </Route>
        </Switch>
      </div>
    </div>
  );
};
